import { tool as clientCreateAgentTool } from "@/shared/domain/agentThread/clientTools/clientCreateAgent";
import { stashPendingKickoff } from "@/shared/domain/agentThread/pendingKickoff";

/**
 * Curated first user-message for the "Build with chat" entry-point on
 * the agents list page. Tells the model to iterate with the user on the
 * agent design, then call {@link clientCreateAgentTool} to seed the
 * editor when the spec is concrete.
 *
 * Phrased as the user's first turn rather than a system prompt because
 * agent threads start in `USER_TURN` — the model's first reply is what
 * the user reads, and a model-authored opening would require a system
 * prompt detour that doesn't carry the same conversational tone.
 *
 * The tool name is interpolated from the tool spec so a future rename
 * doesn't drift this prose out of sync. The phrasing is deliberately
 * vague about the exact tool token — the model can find the tool from
 * its description even without the literal name, and the name is
 * embedded only as a `tool name:` callout so the user-visible
 * transcript reads naturally.
 */
export const AGENT_BUILDER_KICKOFF_MESSAGE =
  "I'd like to build a re-usable Agent template. Help me iterate on what " +
  "it should do — its name, description, optional model profile, kickoff " +
  "message, goals (dataset summary, or dataset triage with a label " +
  "vocabulary), and any variables the agent should accept at launch time. " +
  "Ask follow-up questions as needed. When we land on something concrete, " +
  `use the agent-editor tool (\`${clientCreateAgentTool.spec.name}\`) to ` +
  "open the editor pre-filled with the draft so I can review and save it.";

/**
 * Stash the agent-builder kickoff message and announce it.
 *
 * Indirection over `stashPendingKickoff` so future kickoff variants
 * (e.g., parameterized by a dataset context the user was just looking
 * at) can extend this helper without touching every call site.
 *
 * Opening the AI agent thread collaboration bar is the caller's responsibility
 * — the bar is React-context state that can't be touched from a plain
 * module function, and the contract for what "open the thread" means is
 * caller-specific (today: `useCollaborationBar().setActiveMode("ai-agent-chat")`).
 */
export function stashAgentBuilderKickoff(): void {
  stashPendingKickoff(AGENT_BUILDER_KICKOFF_MESSAGE);
}
